import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AuthClient } from '@dfinity/auth-client';
import { Actor, HttpAgent } from '@dfinity/agent';
import { Principal } from '@dfinity/principal';

interface Charter {
  id: string;
  title: string;
  content: string;
  version: string;
}

type AuthState = 
  | 'loading' 
  | 'checking_returning_user'   // NEW: Check if user exists in UMIA
  | 'checking_domain' 
  | 'domain_not_found' 
  | 'authenticating'           // IC II FIRST
  | 'loading_charter'          // THEN charter (only for new users)
  | 'charter_display' 
  | 'creating_account'         // Backend integration
  | 'redirecting' 
  | 'error';

// NetworkRegistry IDL interface
const networkRegistryIdl = ({ IDL }: any) => {
  const DomainType = IDL.Variant({
    'CORE_DOMAIN': IDL.Null,
    'SOVEREIGN_DOMAIN': IDL.Null,
    'PARTNER_DOMAIN': IDL.Null,
  });
  const FederationInfo = IDL.Record({
    'is_active': IDL.Bool,
    'canister_id': IDL.Principal,
    'domain_type': DomainType,
    'compliance_score': IDL.Float64,
  });
  return IDL.Service({
    'get_federation_info': IDL.Func([IDL.Text], [IDL.Opt(FederationInfo)], ['query']),
  });
};

// UMIA IDL interface
const umiaIdl = ({ IDL }: any) => {
  const AgentType = IDL.Variant({
    'HUMAN': IDL.Principal,
    'AI': IDL.Record({
      'agent_signature': IDL.Text,
      'creation_context': IDL.Text,
      'creator_domain': IDL.Principal,
    }),
    'GUARDIAN': IDL.Record({
      'capabilities': IDL.Vec(IDL.Text),
      'domain': IDL.Text,
      'guardian_name': IDL.Text,
      'personality_hash': IDL.Text,
    }),
  });
  const NetworkStatus = IDL.Variant({
    'ACTIVE': IDL.Null,
    'INACTIVE': IDL.Null,
    'SUSPENDED': IDL.Null,
  });
  const GuardianConfig = IDL.Record({
    'capabilities': IDL.Vec(IDL.Text),
    'coordination_role': IDL.Variant({
      'CoordinationHub': IDL.Null,
      'DomainGuardian': IDL.Null,
      'NetworkOrchestrator': IDL.Null,
    }),
    'domain': IDL.Text,
    'guardian_name': IDL.Text,
    'umia_registration': IDL.Text,
  });
  const UniversalMimiIdentity = IDL.Record({
    'agent_type': AgentType,
    'creation_date': IDL.Int,
    'guardian_config': IDL.Opt(GuardianConfig),
    'issuing_domain': IDL.Principal,
    'last_activity': IDL.Int,
    'network_status': NetworkStatus,
    'participant_metadata': IDL.Text,
    'universal_id': IDL.Text,
  });
  const SimpleResult = IDL.Variant({
    'ok': IDL.Text,
    'err': IDL.Text,
  });
  return IDL.Service({
    'is_human_participant': IDL.Func([IDL.Principal], [IDL.Bool], ['query']),
    'get_human_identity': IDL.Func([IDL.Principal], [IDL.Opt(UniversalMimiIdentity)], ['query']),
    'create_human_identity': IDL.Func([IDL.Principal], [SimpleResult], []),
    'update_human_activity': IDL.Func([IDL.Principal], [SimpleResult], []),
  });
};

// NetworkSession IDL interface
const networkSessionIdl = ({ IDL }: any) => {
  return IDL.Service({
    'create_human_session': IDL.Func([IDL.Principal, IDL.Text], [IDL.Text], []),
    'is_session_valid': IDL.Func([IDL.Principal], [IDL.Bool], ['query']),
  });
};

// DocumentRepository IDL interface (matching ACTUAL deployed canister - complex interface)
const documentRepositoryIdl = ({ IDL }: any) => {
  const DocumentVersion = IDL.Text;
  const DocumentType = IDL.Variant({
    'Charter': IDL.Null,
    'Guide': IDL.Null,
    'Policy': IDL.Null,
    'Template': IDL.Null,
    'Other': IDL.Text,
  });
  const DocumentId = IDL.Text;
  const Document = IDL.Record({
    'content': IDL.Text,
    'createdAt': IDL.Int,
    'createdBy': IDL.Principal,
    'documentType': DocumentType,
    'id': DocumentId,
    'title': IDL.Text,
    'updatedAt': IDL.Int,
    'version': DocumentVersion,
  });
  const DocumentResult_1 = IDL.Variant({
    'ok': Document,
    'err': IDL.Text,
  });
  return IDL.Service({
    'getCharter': IDL.Func([], [DocumentResult_1], []), // No 'query' - it's an update method
    'greet': IDL.Func([IDL.Text], [IDL.Text], ['query']),
  });
};

const GenkanService: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [authState, setAuthState] = useState<AuthState>('loading');
  const [charter, setCharter] = useState<Charter | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [authClient, setAuthClient] = useState<AuthClient | null>(null);
  const [userPrincipal, setUserPrincipal] = useState<Principal | null>(null);
  const [charterAccepted, setCharterAccepted] = useState<boolean>(false);
  const [isReturningUser, setIsReturningUser] = useState<boolean>(false);
  
  const originDomain = searchParams.get('origin');

  // Real canister IDs from deployment inventory
  const CANISTER_IDS = {
    networkRegistry: 'pyu5n-dqaaa-aaaap-qp5xq-cai',
    documentRepository: 'xkudz-6qaaa-aaaaj-qnq6q-cai', 
    umia: 'guzvm-7yaaa-aaaaf-qaxea-cai',
    giftEconomy: '4tdxt-iaaaa-aaaaj-qnrbq-cai',
    networkSession: 'de7so-3qaaa-aaaai-q32la-cai'
  };

  // Initialize auth client and start with IC II authentication FIRST
  useEffect(() => {
    const initAuth = async () => {
      try {
        console.log('Initializing AuthClient...');
        const client = await AuthClient.create();
        console.log('AuthClient created successfully');
        setAuthClient(client);
        
        if (!originDomain) {
          setAuthState('error');
          setErrorMessage('No origin domain specified');
          return;
        }

        console.log('Starting IC Internet Identity authentication FIRST');
        await handleAuthenticationWithClient(client);
      } catch (error) {
        console.error('Failed to initialize authentication:', error);
        setAuthState('error');
        setErrorMessage('Failed to initialize authentication');
      }
    };

    initAuth();
  }, [originDomain]);

  // Create actor helpers
  const createUmiaActor = async () => {
    const agent = new HttpAgent({ host: 'https://ic0.app' });
    return Actor.createActor(umiaIdl, {
      agent,
      canisterId: CANISTER_IDS.umia,
    });
  };

  const createNetworkSessionActor = async () => {
    const agent = new HttpAgent({ host: 'https://ic0.app' });
    return Actor.createActor(networkSessionIdl, {
      agent,
      canisterId: CANISTER_IDS.networkSession,
    });
  };

  const createDocumentRepositoryActor = async () => {
    const agent = new HttpAgent({ host: 'https://ic0.app' });
    // DocumentRepository getCharter is a query method, should work without auth
    return Actor.createActor(documentRepositoryIdl, {
      agent,
      canisterId: CANISTER_IDS.documentRepository,
    });
  };

  const createNetworkRegistryActor = async () => {
    const agent = new HttpAgent({ host: 'https://ic0.app' });
    return Actor.createActor(networkRegistryIdl, {
      agent,
      canisterId: CANISTER_IDS.networkRegistry,
    });
  };

  // Check if user is already a network participant
  const checkReturningUser = async (principal: Principal) => {
    setAuthState('checking_returning_user');
    
    try {
      console.log('Checking if user is returning participant...');
      const umiaActor = await createUmiaActor();
      const isParticipant: any = await umiaActor.is_human_participant(principal);
      
      console.log('Is returning user:', isParticipant);
      setIsReturningUser(isParticipant);
      
      if (isParticipant) {
        console.log('Returning user detected! Checking existing session...');
        const sessionActor = await createNetworkSessionActor();
        const hasValidSession: any = await sessionActor.is_session_valid(principal);
        
        if (hasValidSession) {
          console.log('Valid session exists, proceeding to redirect...');
          await createReturnRedirect(principal);
        } else {
          console.log('No valid session, creating new session for returning user...');
          await createReturningUserSession(principal);
        }
      } else {
        console.log('New user detected, proceeding to domain validation...');
        await lookupDomain(originDomain!);
      }
      
    } catch (error) {
      console.error('Failed to check returning user status:', error);
      // Fallback to normal flow for new users
      await lookupDomain(originDomain!);
    }
  };

  // Create session for returning user (skip charter)
  const createReturningUserSession = async (principal: Principal) => {
    try {
      const umiaId = principal.toText();
      const sessionActor = await createNetworkSessionActor();
      const sessionId: any = await sessionActor.create_human_session(principal, umiaId);
      
      console.log('New session created for returning user:', sessionId);
      
      // Update activity in UMIA
      const umiaActor = await createUmiaActor();
      await umiaActor.update_human_activity(principal);
      
      await createReturnRedirect(principal, false); // firstTime = false
      
    } catch (error) {
      console.error('Failed to create returning user session:', error);
      setAuthState('error');
      setErrorMessage('Failed to create session for returning user');
    }
  };

  // Create redirect for returning users
  const createReturnRedirect = async (principal: Principal, isFirstTime: boolean = true) => {
    setAuthState('redirecting');
    
    const umiaId = principal.toText();
    const sessionId = `session_${Date.now()}_${principal.toText().slice(0, 8)}`;
    
    const returnUrl = new URL(`https://${originDomain}`);
    returnUrl.searchParams.set('session', sessionId);
    returnUrl.searchParams.set('umiaId', umiaId);
    returnUrl.searchParams.set('federationStatus', 'authenticated');
    returnUrl.searchParams.set('firstTime', isFirstTime.toString());
    
    setTimeout(() => {
      console.log('Redirecting returning user to:', returnUrl.toString());
      window.location.href = returnUrl.toString();
    }, 1500);
  };

  // Handle IC Internet Identity authentication with client passed directly
  const handleAuthenticationWithClient = async (client: AuthClient) => {
    console.log('handleAuthenticationWithClient called');
    
    if (!client) {
      console.log('ERROR: client is null, cannot proceed');
      setAuthState('error');
      setErrorMessage('Authentication client not initialized');
      return;
    }

    setAuthState('authenticating');

    try {
      await client.login({
        identityProvider: process.env.DFX_NETWORK === 'local' 
          ? `http://localhost:4943?canisterId=rdmx6-jaaaa-aaaaa-aaadq-cai`
          : 'https://identity.ic0.app',
        onSuccess: async () => {
          console.log('IC Internet Identity login successful!');
          const identity = client.getIdentity();
          const principal = identity.getPrincipal();
          setUserPrincipal(principal);
          
          console.log('Principal authenticated:', principal.toText());
          
          // AFTER authentication, check if returning user FIRST
          await checkReturningUser(principal);
        }
      });
    } catch (error) {
      console.error('Login failed:', error);
      setAuthState('error');
      setErrorMessage('Login process failed');
    }
  };

  // Look up domain in Network Registry (for new users only)
  const lookupDomain = async (domain: string) => {
    setAuthState('checking_domain');
    
    try {
      console.log('Querying NetworkRegistry canister:', CANISTER_IDS.networkRegistry, 'for domain:', domain);
      
      const networkRegistry = await createNetworkRegistryActor();
      const result: any = await networkRegistry.get_federation_info(domain);
      console.log('NetworkRegistry response:', result);
      
      if (result && result[0]) {
        const federationInfo = result[0];
        console.log('Domain found:', federationInfo);
        
        console.log('Checking compliance:', {
          is_active: federationInfo.is_active,
          compliance_score: federationInfo.compliance_score,
          score_check: federationInfo.compliance_score >= 0.7
        });
        
        if (federationInfo.is_active && federationInfo.compliance_score >= 0.7) {
          console.log('Domain validated! Loading charter for new user...');
          await loadCharter();
        } else {
          console.log('Domain validation failed:', {
            is_active: federationInfo.is_active,
            compliance_score: federationInfo.compliance_score
          });
          setAuthState('domain_not_found');
          setErrorMessage(`Domain ${domain} does not meet network compliance requirements`);
        }
      } else {
        console.log('Domain not found in registry');
        setAuthState('domain_not_found');
        setErrorMessage(`Domain ${domain} is not registered in the Mimi Network`);
      }
    } catch (error) {
      console.error('Failed to lookup domain:', error);
      setAuthState('domain_not_found');
      setErrorMessage(`Domain ${domain} is not registered in the Mimi Network Internet Computer Protocol`);
    }
  };

  // Load charter from DocumentRepository - FIXED TO HANDLE VARIANT RESPONSE
  const loadCharter = async () => {
    setAuthState('loading_charter');
    
    try {
      console.log('Loading charter from DocumentRepository canister:', CANISTER_IDS.documentRepository);
      
      const docRepoActor = await createDocumentRepositoryActor();
      console.log('DocumentRepository actor created, calling getCharter...');
      
      const charterResult: any = await docRepoActor.getCharter();
      console.log('getCharter response:', charterResult);
      
      // FIXED: Handle variant { ok = Document } or { err = string } response
      if (charterResult && charterResult.ok) {
        // Extract the Document from the 'ok' variant
        const documentData = charterResult.ok;
        
        const loadedCharter: Charter = {
          id: documentData.id,
          title: documentData.title,
          content: documentData.content,
          version: documentData.version,
        };
        
        console.log('Charter loaded successfully:', loadedCharter.title);
        setCharter(loadedCharter);
        setAuthState('charter_display');
      } else if (charterResult && charterResult.err) {
        console.log('Charter call failed or returned error:', charterResult.err);
        throw new Error(`Charter loading failed: ${charterResult.err}`);
      } else {
        console.log('Charter call returned unexpected format, using fallback');
        // Use fallback charter
        const fallbackCharter: Charter = {
          id: 'charter_fallback',
          title: 'Mimi Network Charter',
          content: 'Welcome to the Mimi Network! By joining this federated network of AI-human collaborative communities, you agree to:\n\n• Engage in ethical collaboration between humans and AI\n• Respect the digital sovereignty of each domain\n• Contribute to the collective flourishing of the network\n• Maintain the principles of the Work-Study-Play framework\n\nThis network uses Internet Computer Protocol for unhijackable digital sovereignty.',
          version: '1.0.0-fallback',
        };
        
        console.log('Using fallback charter');
        setCharter(fallbackCharter);
        setAuthState('charter_display');
      }
    } catch (error) {
      console.error('Charter loading error details:', error);
      setAuthState('error');
      setErrorMessage(`Failed to load network charter: ${String(error)}`);
    }
  };

  // Handle charter acceptance and proceed to account creation (NEW USERS ONLY)
  const handleCharterAcceptance = async () => {
    if (!userPrincipal || !charterAccepted) {
      if (!charterAccepted) {
        setErrorMessage('Please read and accept the charter to continue.');
      }
      return;
    }

    await createNetworkAccount(userPrincipal);
  };

  // Create network account with REAL backend integration
  const createNetworkAccount = async (principal: Principal) => {
    setAuthState('creating_account');

    try {
      const umiaId = principal.toText(); // Use IC principal as UMIA ID
      
      console.log('=== CREATING NEW NETWORK ACCOUNT ===');
      console.log('Principal:', umiaId);
      
      // 1. Register with UMIA canister (REAL CALL)
      console.log('Step 1: Creating UMIA identity...');
      const umiaActor = await createUmiaActor();
      const umiaResult: any = await umiaActor.create_human_identity(principal);
      
      if (umiaResult.err) {
        throw new Error(`UMIA registration failed: ${umiaResult.err}`);
      }
      
      console.log('UMIA identity created successfully:', umiaResult.ok);
      
      // 2. Create network session (REAL CALL)
      console.log('Step 2: Creating network session...');
      const sessionActor = await createNetworkSessionActor();
      const sessionId: any = await sessionActor.create_human_session(principal, umiaId);
      
      console.log('Network session created:', sessionId);
      
      // 3. TODO: Create domain-specific account (depends on origin domain)
      console.log('Step 3: Domain-specific account creation for:', originDomain);
      // This would call the appropriate domain's backend (GiftEconomy for aibento, etc.)
      
      // 4. Create redirect with real session data
      const returnUrl = new URL(`https://${originDomain}`);
      returnUrl.searchParams.set('session', sessionId);
      returnUrl.searchParams.set('umiaId', umiaId);
      returnUrl.searchParams.set('federationStatus', 'authenticated');
      returnUrl.searchParams.set('firstTime', 'true');
      
      setAuthState('redirecting');
      
      setTimeout(() => {
        console.log('Redirecting new user to:', returnUrl.toString());
        window.location.href = returnUrl.toString();
      }, 2000);
      
    } catch (error) {
      console.error('Failed to create network account:', error);
      setAuthState('error');
      setErrorMessage(`Failed to create network account: ${String(error)}`);
    }
  };

  // Beautiful UI components using glassmorphism design
  const renderContent = () => {
    switch (authState) {
      case 'loading':
        return (
          <div style={{
            position: 'absolute',
            bottom: '25%',
            left: '50%',
            transform: 'translateX(-50%)',
            textAlign: 'center'
          }}>
            <div style={{ 
              fontSize: '1.1rem', 
              fontWeight: 300, 
              color: '#333', 
              lineHeight: 1.6 
            }}>
              Initializing{' '}
              <span style={{ color: "#8EB700" }}>federation gateway</span>...
            </div>
          </div>
        );

      case 'checking_returning_user':
        return (
          <div style={{
            position: 'absolute',
            bottom: '25%',
            left: '50%',
            transform: 'translateX(-50%)',
            textAlign: 'center'
          }}>
            <div style={{ 
              fontSize: '1.1rem', 
              fontWeight: 300, 
              color: '#333', 
              lineHeight: 1.6 
            }}>
              Checking{' '}
              <span style={{ color: "#8EB700" }}>network membership</span>...
            </div>
          </div>
        );

      case 'authenticating':
        return (
          <div style={{
            position: 'absolute',
            bottom: '25%',
            left: '50%',
            transform: 'translateX(-50%)',
            textAlign: 'center'
          }}>
            <div style={{ 
              fontSize: '1.1rem', 
              fontWeight: 300, 
              color: '#333', 
              lineHeight: 1.6 
            }}>
              Authenticating via{' '}
              <span style={{ color: "#8EB700" }}>IC Internet Identity</span>...
            </div>
          </div>
        );

      case 'loading_charter':
        return (
          <div style={{
            position: 'absolute',
            bottom: '25%',
            left: '50%',
            transform: 'translateX(-50%)',
            textAlign: 'center'
          }}>
            <div style={{ 
              fontSize: '1.1rem', 
              fontWeight: 300, 
              color: '#333', 
              lineHeight: 1.6 
            }}>
              Loading{' '}
              <span style={{ color: "#8EB700" }}>network charter</span>...
            </div>
          </div>
        );

      case 'charter_display':
        return (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: '600px',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: '20px',
            padding: '2rem',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.15)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
          }}>
            <div style={{ 
              fontSize: '1.5rem', 
              fontWeight: 300, 
              color: '#333', 
              marginBottom: '1.5rem',
              textAlign: 'center'
            }}>
              {charter?.title}
            </div>
            
            <div style={{
              background: 'rgba(248, 249, 250, 0.8)',
              borderRadius: '8px',
              padding: '1.5rem',
              marginBottom: '2rem',
              maxHeight: '300px',
              overflowY: 'auto',
              fontSize: '0.95rem',
              lineHeight: 1.6,
              color: '#444'
            }}>
              {charter?.content.split('\n').map((line, index) => (
                <p key={index} style={{ margin: '0 0 1rem 0' }}>
                  {line}
                </p>
              ))}
            </div>
            
            {errorMessage && (
              <div style={{
                color: '#d32f2f',
                fontSize: '0.9rem',
                marginBottom: '1rem',
                textAlign: 'center'
              }}>
                {errorMessage}
              </div>
            )}

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              alignItems: 'center'
            }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                fontSize: '0.95rem',
                color: '#333'
              }}>
                <input
                  type="checkbox"
                  checked={charterAccepted}
                  onChange={(e) => setCharterAccepted(e.target.checked)}
                  style={{ 
                    marginRight: '0.75rem',
                    transform: 'scale(1.2)'
                  }}
                />
                I have read and acknowledge the network charter
              </label>

              <button
                onClick={handleCharterAcceptance}
                disabled={!charterAccepted}
                style={{
                  background: charterAccepted ? '#8EB700' : '#ddd',
                  color: charterAccepted ? 'white' : '#999',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontSize: '1rem',
                  fontWeight: 300,
                  cursor: charterAccepted ? 'pointer' : 'not-allowed',
                  transition: 'all 0.2s ease'
                }}
              >
                Enter Network
              </button>
            </div>
          </div>
        );

      case 'creating_account':
        return (
          <div style={{
            position: 'absolute',
            bottom: '25%',
            left: '50%',
            transform: 'translateX(-50%)',
            textAlign: 'center'
          }}>
            <div style={{ 
              fontSize: '1.1rem', 
              fontWeight: 300, 
              color: '#333', 
              lineHeight: 1.6 
            }}>
              Creating{' '}
              <span style={{ color: "#8EB700" }}>network account</span>...
            </div>
          </div>
        );

      case 'redirecting':
        return (
          <div style={{
            position: 'absolute',
            bottom: '25%',
            left: '50%',
            transform: 'translateX(-50%)',
            textAlign: 'center'
          }}>
            <div style={{ 
              fontSize: '1.1rem', 
              fontWeight: 300, 
              color: '#333', 
              lineHeight: 1.6 
            }}>
              {isReturningUser ? 'Welcome back! ' : 'Welcome to the network! '}
              <span style={{ color: "#8EB700" }}>Redirecting</span>...
            </div>
          </div>
        );

      case 'checking_domain':
        return (
          <div style={{
            position: 'absolute',
            bottom: '25%',
            left: '50%',
            transform: 'translateX(-50%)',
            textAlign: 'center'
          }}>
            <div style={{ 
              fontSize: '1.1rem', 
              fontWeight: 300, 
              color: '#333', 
              lineHeight: 1.6 
            }}>
              Validating{' '}
              <span style={{ color: "#8EB700" }}>{originDomain}</span>...
            </div>
          </div>
        );

      case 'domain_not_found':
        return (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: '500px',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: '20px',
            padding: '2rem',
            textAlign: 'center',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.15)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
          }}>
            <div style={{ 
              fontSize: '1.5rem', 
              fontWeight: 300, 
              color: '#d32f2f', 
              marginBottom: '1rem' 
            }}>
              Domain Not Found
            </div>
            <div style={{ 
              fontSize: '1rem', 
              color: '#666', 
              lineHeight: 1.6 
            }}>
              {errorMessage}
            </div>
          </div>
        );

      case 'error':
        return (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: '500px',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: '20px',
            padding: '2rem',
            textAlign: 'center',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.15)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
          }}>
            <div style={{ 
              fontSize: '1.5rem', 
              fontWeight: 300, 
              color: '#d32f2f', 
              marginBottom: '1rem' 
            }}>
              Error
            </div>
            <div style={{ 
              fontSize: '1rem', 
              color: '#666', 
              lineHeight: 1.6 
            }}>
              {errorMessage}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      background: `
        radial-gradient(ellipse at top left, rgba(142, 183, 0, 0.15) 0%, transparent 50%),
        radial-gradient(ellipse at bottom right, rgba(142, 183, 0, 0.1) 0%, transparent 50%),
        linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)
      `,
      overflow: 'hidden',
      fontFamily: '"SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      {/* Animated background elements */}
      <div style={{
        position: 'absolute',
        top: '10%',
        right: '15%',
        width: '300px',
        height: '300px',
        background: 'rgba(142, 183, 0, 0.08)',
        borderRadius: '50%',
        filter: 'blur(40px)',
        animation: 'float 6s ease-in-out infinite'
      }} />
      
      <div style={{
        position: 'absolute',
        bottom: '15%',
        left: '10%',
        width: '200px',
        height: '200px',
        background: 'rgba(142, 183, 0, 0.06)',
        borderRadius: '50%',
        filter: 'blur(30px)',
        animation: 'float 8s ease-in-out infinite reverse'
      }} />

      {/* Main content */}
      {renderContent()}

      {/* CSS animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
      `}</style>
    </div>
  );
};

export default GenkanService;// Charter Fix Deploy: Mon Aug  4 13:33:20 UTC 2025 - 59553aef-cd49-49ec-9b79-82ceb84d57ec
